import { Helmet } from "react-helmet-async";
import { ReactNode } from "react";
import { BUSINESS } from "@/data/business";

export const SITE_URL = BUSINESS.url;
export const SITE_NAME = BUSINESS.name;

/** Absolute canonical URL for a given path. */
export const buildCanonical = (path = "/") =>
  `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;

export const DEFAULT_OG_IMAGE =
  "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/dfa1bdc4-74f6-4548-9c34-38ec1d790bba/id-preview-9c1da243--22201b90-9930-4e46-9266-2ddfc15c9874.lovable.app-1773414547456.png";

export interface SeoProps {
  title: string;
  description: string;
  /** Path part, e.g. "/kontakt". Falls back to "/" */
  path?: string;
  image?: string;
  type?: "website" | "article" | "profile";
  /** Defaults to indexable with max-image-preview:large. Pass "noindex,nofollow" to hide. */
  robots?: string;
  /** JSON-LD objects to render as <script type="application/ld+json"> tags. */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  /** Optional extra Helmet children (rare). */
  children?: ReactNode;
}

// Escape </ to prevent script-tag breakout inside JSON-LD.
const safeJson = (obj: unknown) =>
  JSON.stringify(obj).replace(/</g, "\\u003c");

const Seo = ({
  title,
  description,
  path = "/",
  image = DEFAULT_OG_IMAGE,
  type = "website",
  robots = "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
  jsonLd,
  children,
}: SeoProps) => {
  const url = `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;
  const ldArray = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  return (
    <Helmet prioritizeSeoTags>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robots} />
      <link rel="canonical" href={url} />

      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="de_DE" />
      <meta property="og:image" content={image} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {ldArray.map((obj, i) => (
        <script key={i} type="application/ld+json">{safeJson(obj)}</script>
      ))}

      {children}
    </Helmet>
  );
};

export default Seo;

// ---------- JSON-LD helpers ----------

export const breadcrumbLd = (items: { name: string; path: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${SITE_URL}${it.path}`,
  })),
});

export const faqLd = (qa: { q: string; a: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: qa.map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  })),
});

// Speakable schema intentionally removed — only relevant for news content.


export const apartmentLd = (apt: {
  id: string;
  name: string;
  description: string;
  image?: string;
  size?: string;
  maxGuests?: number;
  rooms?: number;
  house: string;
  url: string;
}) => {
  // Parse floor size: extract a number from strings like "60", "60 m²", "ca. 60 m²".
  // Only emit floorSize when a valid positive number is found.
  const sizeMatch = apt.size ? String(apt.size).match(/(\d+(?:[.,]\d+)?)/) : null;
  const sizeValue = sizeMatch ? parseFloat(sizeMatch[1].replace(",", ".")) : NaN;
  const hasValidSize = Number.isFinite(sizeValue) && sizeValue > 0;

  const ld: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Apartment",
    name: apt.name,
    description: apt.description,
    url: `${SITE_URL}${apt.url}`,
    address: {
      "@type": "PostalAddress",
      addressLocality: "Cuxhaven",
      postalCode: "27476",
      addressCountry: "DE",
    },
  };

  if (apt.image) ld.image = apt.image;
  if (typeof apt.rooms === "number" && apt.rooms > 0) ld.numberOfRooms = apt.rooms;
  if (typeof apt.maxGuests === "number" && apt.maxGuests > 0) {
    ld.occupancy = { "@type": "QuantitativeValue", maxValue: apt.maxGuests };
  }
  if (hasValidSize) {
    ld.floorSize = { "@type": "QuantitativeValue", value: sizeValue, unitCode: "MTK" };
  }

  return ld;
};
